import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../lib/queryClient'
import authService from '../services/authService'
import { useAuth } from '../contexts/AuthContext'

// Types
interface LoginCredentials {
  username: string
  password: string
}

interface LoginResponse {
  success: boolean
  data: {
    user: any
    token: string
    refreshToken: string
  }
  message: string
}

// Query hook for getting current user
export const useCurrentUser = () => {
  const { token } = useAuth()
  
  return useQuery({
    queryKey: queryKeys.user,
    queryFn: async () => {
      const response = await authService.getCurrentUser()
      return response.data
    },
    enabled: !!token, // Only run query if token exists
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401 (unauthorized)
      if (error instanceof Error && 'status' in error) {
        const status = (error as any).status
        if (status === 401) return false
      }
      return failureCount < 2
    },
  })
}

// Mutation hook for login
export const useLogin = () => {
  const queryClient = useQueryClient()
  const { login: authLogin } = useAuth()
  
  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      const response = await authService.login(credentials)
      return response
    },
    onSuccess: (data: LoginResponse) => {
      if (data.success) {
        // Use existing AuthContext login method
        authLogin(data.data.token, data.data.user)
        
        // Set user data in query cache
        queryClient.setQueryData(queryKeys.user, data.data.user)
        
        // Invalidate any cached data that might be user-specific
        queryClient.invalidateQueries({ queryKey: ['notifications'] })
        queryClient.invalidateQueries({ queryKey: ['users'] })
        queryClient.invalidateQueries({ queryKey: ['centers'] })
        queryClient.invalidateQueries({ queryKey: ['branches'] })
        queryClient.invalidateQueries({ queryKey: ['fees'] })
        queryClient.invalidateQueries({ queryKey: ['transfers'] })
      }
    },
    onError: (error) => {
      console.error('Login mutation error:', error)
      // Clear any existing auth data on error
      localStorage.removeItem('authToken')
      localStorage.removeItem('refreshToken')
      queryClient.clear()
    },
  })
}

// Enable why-did-you-render tracking for this hook
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  try {
    const whyDidYouRender = require('@welldone-software/why-did-you-render');
    
    // Track this hook's re-renders
    (useLogin as any).whyDidYouRender = true;
    
    console.log('🔍 useLogin hook tracking enabled');
  } catch (error) {
    console.warn('why-did-you-render failed to load for useLogin:', error);
  }
}

// Mutation hook for logout
export const useLogout = () => {
  const queryClient = useQueryClient()
  const { logout: authLogout } = useAuth()
  
  return useMutation({
    mutationFn: async () => {
      const response = await authService.logout()
      return response
    },
    onSuccess: () => {
      // Use existing AuthContext logout method
      authLogout()
      
      // Clear all query cache
      queryClient.clear()
    },
    onError: (error) => {
      console.error('Logout mutation error:', error)
      // Still use auth logout even if server logout fails
      authLogout()
      queryClient.clear()
    },
  })
}

// Mutation hook for refreshing token
export const useRefreshToken = () => {
  const queryClient = useQueryClient()
  const { login: authLogin } = useAuth()
  
  return useMutation({
    mutationFn: async () => {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }
      
      const response = await authService.refreshToken(refreshToken)
      return response
    },
    onSuccess: (data) => {
      if (data.success) {
        // Update tokens using existing AuthContext login method
        authLogin(data.data.token, data.data.user)
        
        // Update user data in cache
        queryClient.setQueryData(queryKeys.user, data.data.user)
      }
    },
    onError: (error) => {
      console.error('Token refresh error:', error)
      // Clear auth data on refresh failure
      localStorage.removeItem('authToken')
      localStorage.removeItem('refreshToken')
      queryClient.clear()
    },
  })
}

// Hook for checking authentication status
export const useAuthStatus = () => {
  const { token, user, isLoading: contextLoading } = useAuth()
  const { data: currentUser, isLoading: queryLoading, error } = useCurrentUser()
  
  return {
    isAuthenticated: !!token && !!currentUser,
    user: currentUser || user,
    isLoading: contextLoading || queryLoading,
    error,
    token,
  }
}
